import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../contexts/WalletProvider';
import { useMarket } from '../../contexts/MarketProvider';
import { useMarketTransactions, CreateMarketParams } from '../../hooks/useMarketTransactions';
import { imageUploadService } from '../../services/imageUploadService';
import { marketApiService } from '../../services/marketApiService';
import './CreateMarket.css';

// ============================================================================
// INPUT SANITIZATION - Security against XSS and injection attacks
// ============================================================================

/**
 * Sanitize text input to prevent XSS and injection attacks
 * - Removes HTML tags
 * - Escapes dangerous characters
 * - Normalizes whitespace
 * - Removes control characters
 */
const sanitizeText = (input: string): string => {
  if (!input) return '';
  
  let result = input;
  
  // Remove HTML tags
  result = result.replace(/<[^>]*>/g, '');
  // Remove script-like patterns
  result = result.replace(/javascript:/gi, '');
  result = result.replace(/on\w+\s*=/gi, '');
  // Remove null bytes and other problematic control characters
  // eslint-disable-next-line no-control-regex
  result = result.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  // Normalize multiple spaces to single space
  result = result.replace(/\s+/g, ' ');
  // Trim whitespace
  return result.trim();
};

/**
 * Validate text doesn't contain suspicious patterns
 */
const containsSuspiciousPatterns = (text: string): boolean => {
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /data:/i,
    /vbscript:/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /\{\{.*\}\}/,  // Template injection
    /\$\{.*\}/,    // Template literal injection
    /<\?/,         // PHP tags
    /<%/,          // ASP tags
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(text));
};

// ============================================================================
// CHARACTER VALIDATION - Block dangerous Unicode while allowing emojis
// ============================================================================

/**
 * Dangerous Unicode patterns to block:
 * - Zero-width characters (U+200B zero-width space, U+200C zero-width non-joiner, U+FEFF BOM, U+2060 word joiner)
 *   NOTE: U+200D (ZWJ) is NOT blocked here — it's required for compound emojis (👨‍👩‍👧, 👩‍💻, etc.)
 * - RTL/LTR override characters (U+202A-U+202E, U+2066-U+2069)
 * - Other invisible formatting (U+00AD soft hyphen, U+034F combining grapheme joiner)
 * - Homoglyph-prone scripts (Cyrillic, Greek letters that look like Latin)
 */
const DANGEROUS_UNICODE_PATTERN = /[\u200B\u200C\uFEFF\u2060\u202A-\u202E\u2066-\u2069\u00AD\u034F\u0400-\u04FF\u0370-\u03FF]/;

/**
 * Basic allowed ASCII characters (letters, numbers, punctuation, space)
 */
const SAFE_ASCII_PATTERN = /^[a-zA-Z0-9\s.,!?'"\-:()%$@#&+=/]*$/;

/**
 * Check if a character is an emoji (simplified check for common emoji ranges)
 * Covers: emoticons, dingbats, symbols, flags, etc.
 */
const isEmoji = (char: string): boolean => {
  const code = char.codePointAt(0) || 0;
  return (
    (code >= 0x1F300 && code <= 0x1F9FF) || // Misc symbols, emoticons, food, etc.
    (code >= 0x2600 && code <= 0x26FF) ||   // Misc symbols (sun, cloud, etc.)
    (code >= 0x2700 && code <= 0x27BF) ||   // Dingbats
    (code >= 0x1F600 && code <= 0x1F64F) || // Emoticons
    (code >= 0x1F680 && code <= 0x1F6FF) || // Transport/map symbols
    (code >= 0x1FA00 && code <= 0x1FAFF) || // Chess, symbols, etc.
    (code >= 0x1F1E0 && code <= 0x1F1FF) || // Regional indicator symbols (flags)
    (code >= 0xFE00 && code <= 0xFE0F) ||   // Variation selectors (for emoji presentation)
    (code >= 0x1FA70 && code <= 0x1FAFF) || // Symbols and Pictographs Extended-A
    (code >= 0xE0020 && code <= 0xE007F) || // Tags block (used in flag sequences like 🏴󠁧󠁢󠁥󠁮󠁧󠁿)
    (code >= 0x231A && code <= 0x23FF) ||   // Misc technical (⌚, ⏰, etc.)
    (code >= 0x2300 && code <= 0x23FF) ||   // Misc technical symbols
    (code >= 0x2B50 && code <= 0x2B55) ||   // Stars, circles (⭐, ⭕)
    (code >= 0x2934 && code <= 0x2935) ||   // Arrows
    (code >= 0x25AA && code <= 0x25FE) ||   // Geometric shapes (▪️, ◾, etc.)
    (code >= 0x2190 && code <= 0x21FF) ||   // Arrows
    (code >= 0x2100 && code <= 0x214F) ||   // Letterlike symbols (™, ℹ, etc.)
    (code >= 0x2010 && code <= 0x2027) ||   // General punctuation (‐, –, —, etc.)
    (code >= 0x203C && code <= 0x2049) ||   // ‼️, ⁉️
    (code >= 0x20E3 && code <= 0x20E3) ||   // Combining enclosing keycap
    (code >= 0xE0100 && code <= 0xE01EF) || // Variation Selectors Supplement
    code === 0x200D ||                       // Zero-width joiner (needed for compound emojis)
    code === 0x20E3 ||                       // Combining enclosing keycap (for keycap emojis like 1️⃣)
    code === 0x00A9 ||                       // © Copyright
    code === 0x00AE ||                       // ® Registered
    code === 0x2122                          // ™ Trademark
  );
};

/**
 * Validate text doesn't contain dangerous Unicode characters
 * Allows: ASCII letters/numbers/punctuation + emojis (including compound emojis with ZWJ)
 * Blocks: invisible chars, RTL overrides, homoglyph scripts
 * 
 * Strategy: Check each character individually so that emoji-related code points
 * (ZWJ U+200D, variation selectors) are allowed in emoji context but standalone
 * dangerous invisible characters are still blocked.
 */
const containsOnlySafeCharacters = (text: string): boolean => {
  if (!text) return true;
  
  // Use Array.from to properly iterate over Unicode code points (handles surrogate pairs)
  const chars = Array.from(text);
  
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i];
    
    // Allow safe ASCII characters
    if (SAFE_ASCII_PATTERN.test(char)) {
      continue;
    }
    
    // Allow emoji characters (includes ZWJ U+200D and variation selectors U+FE00-FE0F)
    if (isEmoji(char)) {
      continue;
    }
    
    // Everything else (dangerous invisible chars, Cyrillic, Greek, etc.) is blocked
    return false;
  }
  
  return true;
};

/**
 * Get a user-friendly message about invalid characters
 */
const getInvalidCharacterMessage = (text: string, fieldName: string): string => {
  // Check for dangerous Unicode patterns (excluding ZWJ which is used in emojis)
  if (DANGEROUS_UNICODE_PATTERN.test(text)) {
    // Check specifically for invisible characters (ZWJ U+200D excluded — it's emoji-safe)
    if (/[\u200B\u200C\uFEFF\u2060\u00AD\u034F]/.test(text)) {
      return `${fieldName} contains invisible characters. Please remove any hidden formatting.`;
    }
    if (/[\u202A-\u202E\u2066-\u2069]/.test(text)) {
      return `${fieldName} contains text direction control characters. Please use plain text.`;
    }
    if (/[\u0400-\u04FF]/.test(text)) {
      return `${fieldName} contains Cyrillic characters. Please use standard Latin letters.`;
    }
    if (/[\u0370-\u03FF]/.test(text)) {
      return `${fieldName} contains Greek characters. Please use standard Latin letters.`;
    }
  }
  
  // Find the first non-allowed character
  for (const char of text) {
    const isSafeAscii = SAFE_ASCII_PATTERN.test(char);
    if (!isSafeAscii && !isEmoji(char)) {
      return `${fieldName} contains an unsupported character: "${char}". Please use letters, numbers, punctuation, or emojis.`;
    }
  }
  
  return `${fieldName} contains invalid characters.`;
};

type DurationUnit = 'minutes' | 'hours' | 'days';

interface FormState {
  question: string;
  options: string[];
  durationValue: number;
  durationUnit: DurationUnit;
  imageFile: File | null;
  imagePreview: string | null;
}

type CreateStep = 'form' | 'uploading' | 'creating' | 'registering' | 'success' | 'partial' | 'error';

/**
 * Check if an option at a given index is a duplicate of any previous option
 * Returns the index of the first occurrence if duplicate, -1 otherwise
 */
const findDuplicateIndex = (options: string[], currentIndex: number): number => {
  const currentValue = options[currentIndex].trim().toLowerCase();
  if (!currentValue) return -1;
  
  for (let i = 0; i < currentIndex; i++) {
    if (options[i].trim().toLowerCase() === currentValue) {
      return i;
    }
  }
  return -1;
};

const CreateMarket: React.FC = () => {
  const navigate = useNavigate();
  const { solanaWallet, isAuthenticated } = useWallet();
  const { loadMarkets } = useMarket();
  const { createMarket, isLoading: isTxLoading } = useMarketTransactions(solanaWallet);

  const [step, setStep] = useState<CreateStep>('form');
  const [error, setError] = useState<string | null>(null);
  const [txSignature, setTxSignature] = useState<string | null>(null);
  const [marketAddress, setMarketAddress] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    question: '',
    options: ['', ''],
    durationValue: 24,
    durationUnit: 'hours',
    imageFile: null,
    imagePreview: null,
  });

  /**
   * Calculate the end time based on duration from now
   */
  const calculateEndTime = (): Date => {
    const now = new Date();
    let durationMs = form.durationValue;
    
    switch (form.durationUnit) {
      case 'minutes':
        durationMs *= 60 * 1000;
        break;
      case 'hours':
        durationMs *= 60 * 60 * 1000;
        break;
      case 'days':
        durationMs *= 24 * 60 * 60 * 1000;
        break;
    }
    
    return new Date(now.getTime() + durationMs);
  };

  /**
   * Get human-readable duration text for display
   */
  const getDurationText = (): string => {
    const value = form.durationValue;
    const unit = form.durationUnit;
    
    if (value === 1) {
      // Singular form
      return `1 ${unit.slice(0, -1)}`;
    }
    return `${value} ${unit}`;
  };

  /**
   * Normalize text input: collapse multiple spaces into single space
   */
  const normalizeSpaces = (text: string): string => {
    return text.replace(/  +/g, ' ');
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const normalized = normalizeSpaces(e.target.value);
    setForm(prev => ({ ...prev, question: normalized }));
  };

  const handleOptionChange = (index: number, value: string) => {
    const normalized = normalizeSpaces(value);
    setForm(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = normalized;
      return { ...prev, options: newOptions };
    });
  };

  const addOption = () => {
    if (form.options.length < 4) {
      setForm(prev => ({ ...prev, options: [...prev.options, ''] }));
    }
  };

  const removeOption = (index: number) => {
    if (form.options.length > 2) {
      setForm(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index),
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = imageUploadService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }

      setForm(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file),
      }));
      setError(null);
    }
  };

  const removeImage = () => {
    if (form.imagePreview) {
      URL.revokeObjectURL(form.imagePreview);
    }
    setForm(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: null,
    }));
  };

  const validateForm = useCallback((): string | null => {
    // ========================================================================
    // QUESTION VALIDATION
    // ========================================================================
    const sanitizedQuestion = sanitizeText(form.question);
    
    if (!sanitizedQuestion) {
      return 'Question is required';
    }
    if (sanitizedQuestion.length < 10) {
      return 'Question must be at least 10 characters';
    }
    if (sanitizedQuestion.length > 100) {
      return 'Question must be 100 characters or less';
    }
    if (containsSuspiciousPatterns(form.question)) {
      return 'Question contains invalid characters or patterns';
    }
    // Character validation - block dangerous Unicode while allowing emojis
    if (!containsOnlySafeCharacters(sanitizedQuestion)) {
      return getInvalidCharacterMessage(sanitizedQuestion, 'Question');
    }

    // ========================================================================
    // OPTIONS VALIDATION - All options must be filled
    // ========================================================================
    
    // Check that ALL option fields are filled (not just 2)
    for (let i = 0; i < form.options.length; i++) {
      const option = form.options[i].trim();
      if (!option) {
        return `Option ${i + 1} is required`;
      }
    }
    
    const sanitizedOptions = form.options.map(o => sanitizeText(o));
    
    // Validate each option
    for (let i = 0; i < sanitizedOptions.length; i++) {
      const option = sanitizedOptions[i];
      
      if (option.length < 1) {
        return `Option ${i + 1} cannot be empty`;
      }
      if (option.length > 25) {
        return `Option ${i + 1} must be 25 characters or less`;
      }
      if (containsSuspiciousPatterns(form.options[i])) {
        return `Option ${i + 1} contains invalid characters or patterns`;
      }
      // Character validation - block dangerous Unicode while allowing emojis
      if (!containsOnlySafeCharacters(option)) {
        return getInvalidCharacterMessage(option, `Option ${i + 1}`);
      }
    }
    
    // Check for duplicate options (case-insensitive)
    const lowerOptions = sanitizedOptions.map(o => o.toLowerCase());
    const seen = new Set<string>();
    const duplicates: string[] = [];
    
    for (const opt of lowerOptions) {
      if (seen.has(opt)) {
        duplicates.push(opt);
      }
      seen.add(opt);
    }
    
    if (duplicates.length > 0) {
      return `Duplicate option found: "${duplicates[0]}". All options must be unique.`;
    }
    
    // Ensure minimum of 2 options
    if (sanitizedOptions.length < 2) {
      return 'At least 2 options are required';
    }

    // ========================================================================
    // DURATION VALIDATION
    // ========================================================================
    if (!form.durationValue || form.durationValue < 1) {
      return 'Duration is required';
    }
    
    // Calculate duration in milliseconds
    let durationMs = form.durationValue;
    switch (form.durationUnit) {
      case 'minutes':
        durationMs *= 60 * 1000;
        break;
      case 'hours':
        durationMs *= 60 * 60 * 1000;
        break;
      case 'days':
        durationMs *= 24 * 60 * 60 * 1000;
        break;
    }
    
    // Maximum duration: 30 days
    const maxDurationMs = 30 * 24 * 60 * 60 * 1000;
    if (durationMs > maxDurationMs) {
      return 'Market cannot run for more than 30 days';
    }

    return null;
  }, [form]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isAuthenticated) {
      setError('Please connect your wallet first');
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      let imageUrl: string | undefined;

      // Step 1: Upload image if provided
      if (form.imageFile) {
        setStep('uploading');
        const uploadResult = await imageUploadService.uploadMarketImage(form.imageFile);
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Failed to upload image');
        }
        imageUrl = uploadResult.url;
      }

      // Step 2: Create market on-chain
      setStep('creating');
      
      // Sanitize all text inputs before submission
      const sanitizedQuestion = sanitizeText(form.question);
      const sanitizedOptions = form.options
        .map(o => sanitizeText(o))
        .filter(o => o.length > 0);
      
      // Start time is now, end time is calculated from duration
      const startTime = new Date();
      const endTime = calculateEndTime();
      
      const createParams: CreateMarketParams = {
        question: sanitizedQuestion,
        optionNames: sanitizedOptions,
        startTime,
        endTime,
      };

      const createResult = await createMarket(createParams);
      
      if (!createResult.success || !createResult.signature) {
        throw new Error(createResult.error || 'Failed to create market on-chain');
      }

      setTxSignature(createResult.signature);
      setMarketAddress(createResult.marketAddress || null);

      // Step 3: Register with backend (with retries for tx confirmation)
      setStep('registering');
      
      // Retry registration up to 3 times with delays (tx may not be confirmed yet)
      let registerResult: { success: boolean; error?: string } = { success: false };
      const maxRetries = 3;
      const retryDelays = [2000, 4000, 6000]; // 2s, 4s, 6s delays
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        // Wait before each attempt (including first) to let tx confirm
        await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
        
        registerResult = await marketApiService.registerMarket(
          createResult.signature,
          imageUrl
        );
        
        if (registerResult.success) {
          break; // Success, stop retrying
        }
        
        console.warn(`Registration attempt ${attempt + 1} failed:`, registerResult.error);
      }

      if (!registerResult.success) {
        // Market created on-chain but registration failed after all retries
        setError(registerResult.error || 'Market created on-chain but failed to register. You can try registering again later.');
        setStep('partial');
        return;
      }

      // Refresh the market list so the new market appears
      loadMarkets(1);
      
      setStep('success');

    } catch (err: any) {
      console.error('Create market failed:', err);
      setError(err.message || 'Failed to create market');
      setStep('error');
    }
  };

  const handleViewMarket = () => {
    if (marketAddress) {
      navigate(`/market/${marketAddress}`);
    }
  };

  const handleCreateAnother = () => {
    setStep('form');
    setError(null);
    setTxSignature(null);
    setMarketAddress(null);
    setForm({
      question: '',
      options: ['', ''],
      durationValue: form.durationValue,
      durationUnit: form.durationUnit,
      imageFile: null,
      imagePreview: null,
    });
  };

  const handleRetryRegistration = async () => {
    if (!txSignature) return;
    
    setStep('registering');
    setError(null);
    
    try {
      // Image URL was already uploaded, just retry registration with signature
      const registerResult = await marketApiService.registerMarket(txSignature);
      
      if (!registerResult.success) {
        setError(registerResult.error || 'Registration failed. Please try again.');
        setStep('partial');
        return;
      }
      
      // Refresh the market list so the new market appears
      loadMarkets(1);
      
      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      setStep('partial');
    }
  };

  // Loading/Progress UI
  if (step !== 'form' && step !== 'success' && step !== 'partial' && step !== 'error') {
    return (
      <div className="create-market-container">
        <div className="create-market-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">
            {step === 'uploading' && 'Uploading image...'}
            {step === 'creating' && 'Creating market on-chain...'}
            {step === 'registering' && 'Registering market...'}
          </p>
          <p className="loading-subtext">
            {step === 'registering' 
              ? 'Waiting for transaction confirmation...' 
              : 'Please confirm the transaction in your wallet'}
          </p>
        </div>
      </div>
    );
  }

  // Success UI
  if (step === 'success') {
    return (
      <div className="create-market-container">
        <div className="create-market-success">
          <div className="success-icon">✓</div>
          <h2>Market Created!</h2>
          <p>Your market has been created successfully.</p>
          
          {txSignature && (
            <div className="tx-info">
              <span className="tx-label">Transaction:</span>
              <a 
                href={`https://solscan.io/tx/${txSignature}?cluster=devnet`}
                target="_blank"
                rel="noopener noreferrer"
                className="tx-link"
              >
                {txSignature.slice(0, 8)}...{txSignature.slice(-8)}
              </a>
            </div>
          )}

          <div className="success-actions">
            <button className="btn-primary" onClick={handleViewMarket}>
              View Market
            </button>
            <button className="btn-secondary" onClick={handleCreateAnother}>
              Create Another
            </button>
            <button className="btn-text" onClick={() => navigate('/')}>
              Back to Markets
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Partial Success UI - On-chain succeeded but registration failed
  if (step === 'partial') {
    return (
      <div className="create-market-container">
        <div className="create-market-partial">
          <div className="partial-icon">⚠</div>
          <h2>Market Created On-Chain</h2>
          <p>Your market was created on-chain but couldn't be registered in our database yet.</p>
          <p className="partial-warning">The transaction may still be confirming. Try registering again in a moment.</p>
          
          {txSignature && (
            <div className="tx-info">
              <span className="tx-label">Transaction:</span>
              <a 
                href={`https://solscan.io/tx/${txSignature}?cluster=devnet`}
                target="_blank"
                rel="noopener noreferrer"
                className="tx-link"
              >
                {txSignature.slice(0, 8)}...{txSignature.slice(-8)}
              </a>
            </div>
          )}

          <div className="partial-actions">
            <button className="btn-primary" onClick={handleRetryRegistration}>
              Retry Registration
            </button>
            <button className="btn-text" onClick={() => navigate('/')}>
              Back to Markets
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error UI
  if (step === 'error') {
    return (
      <div className="create-market-container">
        <div className="create-market-error">
          <div className="error-icon">✕</div>
          <h2>Creation Failed</h2>
          <p className="error-message">{error}</p>
          
          <div className="error-actions">
            <button className="btn-primary" onClick={() => setStep('form')}>
              Try Again
            </button>
            <button className="btn-text" onClick={() => navigate('/')}>
              Back to Markets
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form UI
  return (
    <div className="create-market-container">
      <div className="create-market-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back
        </button>
        <h1>Create Market</h1>
      </div>

      <form className="create-market-form" onSubmit={handleSubmit}>
        {error && (
          <div className="form-error">
            {error}
          </div>
        )}

        {/* Question */}
        <div className="form-group">
          <label htmlFor="question">Question</label>
          <div className="input-with-count">
            <textarea
              id="question"
              value={form.question}
              onChange={handleQuestionChange}
              placeholder="What will happen?"
              maxLength={100}
              rows={2}
            />
            <span className="char-count">{form.question.length}/100</span>
          </div>
        </div>

        {/* Options */}
        <div className="form-group">
          <label>Options</label>
          <div className="options-list">
            {form.options.map((option, index) => {
              const duplicateOfIndex = findDuplicateIndex(form.options, index);
              const isDuplicate = duplicateOfIndex !== -1;
              
              return (
                <div key={index} className={`option-row ${isDuplicate ? 'option-duplicate' : ''}`}>
                  <div className="input-with-count">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      maxLength={25}
                      className={isDuplicate ? 'input-error' : ''}
                    />
                    {isDuplicate ? (
                      <span className="duplicate-warning">
                        Same as Option {duplicateOfIndex + 1}
                      </span>
                    ) : (
                      <span className="char-count">{option.length}/25</span>
                    )}
                  </div>
                  {form.options.length > 2 && (
                    <button
                      type="button"
                      className="remove-option-btn"
                      onClick={() => removeOption(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {form.options.length < 4 && (
            <button type="button" className="add-option-btn" onClick={addOption}>
              + Add Option
            </button>
          )}
        </div>

        {/* Duration */}
        <div className="form-group">
          <label htmlFor="duration">Market Duration</label>
          <p className="form-hint">Market starts immediately and ends in {getDurationText()}</p>
          <div className="duration-picker">
            <input
              type="number"
              id="duration"
              min="1"
              max={form.durationUnit === 'days' ? 30 : form.durationUnit === 'hours' ? 720 : 43200}
              value={form.durationValue}
              onChange={(e) => setForm(prev => ({ 
                ...prev, 
                durationValue: Math.max(1, parseInt(e.target.value) || 1)
              }))}
              className="duration-input"
            />
            <select
              value={form.durationUnit}
              onChange={(e) => setForm(prev => ({ 
                ...prev, 
                durationUnit: e.target.value as DurationUnit 
              }))}
              className="duration-unit"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
        </div>

        {/* Image */}
        <div className="form-group">
          <label>Image (Optional)</label>
          {form.imagePreview ? (
            <>
              <div className="image-preview">
                <img src={form.imagePreview} alt="Preview" />
                <button type="button" className="remove-image-btn" onClick={removeImage}>
                  ✕ Remove
                </button>
              </div>
              <p className="image-preview-hint">Image will be cropped to square (1:1)</p>
            </>
          ) : (
            <div className="image-upload">
              <input
                type="file"
                id="image"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageChange}
              />
              <label htmlFor="image" className="image-upload-label">
                <span className="upload-icon">📷</span>
                <span>Click to upload image</span>
                <span className="upload-hint">JPEG, PNG, GIF, WebP (max 10MB) • Cropped to square</span>
              </label>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="form-actions">
          {!isAuthenticated ? (
            <p className="connect-prompt">Connect your wallet to create a market</p>
          ) : (
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isTxLoading}
            >
              {isTxLoading ? 'Creating...' : 'Create Market'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateMarket;
